import { useLayoutEffect, Fragment, useEffect, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "./store/hooks";

import "./App.css";
import Background from "./components/UI/Background";
import LoginForm from "./components/Auth/LoginForm";
import Wrapper from "./components/UI/Wrapper";
import { RootState } from "./store";
import ModalState from "./components/UI/ModalState";
import RefreshBanner from "./components/UI/RefreshBanner";
import { refreshAuthToken } from "./store/auth-actions";
import Header from "./components/UI/Header";
import useBackgroundRefresh from "./hooks/use-background-refresh";
import useDayRollover from "./hooks/use-day-rollover";
import useSessionKeepAlive from "./hooks/use-session-keep-alive";
import FastAddEventForm from "./components/Events/FastAddEventForm";
import Events from "./components/Events/Events";
import { UIActions } from "./store/ui-slice";
import DeleteEventConfirm from "./components/Events/DeleteEventConfirm";
import DiscardChangesConfirm from "./components/Events/DiscardChangesConfirm";
import { fetchEventsData } from "./store/events-actions";

// loaded on demand: hidden on mobile until the user opens the calendar
// overlay or the add/edit form, so they shouldn't block initial paint
const Calendar = lazy(() => import("./components/Calendar/Calendar"));
const EventForm = lazy(() => import("./components/Events/EventForm"));

const App = () => {

  const dispatch = useAppDispatch();
  const { token: isAuth, expiresIn, uid, refreshToken } = useSelector(
    (state: RootState) => state.auth
  );
  const { deviceCalendar, addEvent } = useSelector(
    (state: RootState) => state.ui
  );
  const { eventToEdit } = useSelector(
    (state: RootState) => state.events
  );

  useDayRollover(isAuth);
  useBackgroundRefresh(isAuth);
  useSessionKeepAlive(refreshToken);

  useLayoutEffect(() => {
    function updateSize() {
      dispatch(UIActions.resize({
        width: window.innerWidth,
        height: window.innerHeight
      }));
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [dispatch]);

  useEffect(() => {
    // only re-fetch when the logged-in user actually changes, not on every
    // silent token refresh (uid and token are always updated together)
    if (uid) {
      dispatch(fetchEventsData(uid));
    }
  }, [uid, dispatch]);

  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout>;
    if (isAuth) {
      // refresh the ID token a minute before it expires instead of forcing a re-login
      const refreshIn = Math.max(expiresIn - 60000, 0);
      refreshTimeout = setTimeout(() => {
        // a failure is retried by useSessionKeepAlive
        dispatch(refreshAuthToken(refreshToken)).catch(() => {});
      }, refreshIn);
    } else if (refreshToken) {
      // the stored ID token already expired (e.g. tab was closed) but the refresh token is still valid
      dispatch(refreshAuthToken(refreshToken)).catch((err: Error) => {
        dispatch(UIActions.error({ message: err.message }));
      });
    }

    return () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [isAuth, expiresIn, refreshToken, dispatch]);

  return (
    <Fragment>
      <Background />
      <ModalState />
      <DeleteEventConfirm />
      <DiscardChangesConfirm />
      {/* With a refresh token the session is only being renewed, so the login
          form would be a false alarm: it stays up if the network is down, and
          reads as a logout to anyone who reloads the page. */}
      {!isAuth && !refreshToken && (
        <Wrapper>
          <LoginForm />
        </Wrapper>
      )}
      {isAuth && (
        <Fragment>
          {/* Nothing to sync without a session, so the banner is mounted with
              the logged-in tree. It is position:fixed, so this does not move it. */}
          <RefreshBanner />
          <Header />
          <Suspense fallback={null}>
            {(window.desktop || deviceCalendar) && <Calendar />}
            {(window.desktop || addEvent || eventToEdit !== null) && <EventForm />}
          </Suspense>
          <FastAddEventForm />
          {(window.desktop || (!deviceCalendar && !addEvent && eventToEdit === null)) && <Events />}
        </Fragment>
      )}
    </Fragment>
  );
};

export default App;
